import React, { useMemo, useState } from 'react';
import { useSiteContext } from '../context/SiteContext';
import { getRuntimeHost, getRuntimeOrigin } from '../utils/runtimeSite';

const InvitePage: React.FC = () => {
  const { profile, preferences, topScore } = useSiteContext();
  const [feedback, setFeedback] = useState('');

  const inviterName = profile?.nickname || 'Guest Challenger';
  const hostLabel = getRuntimeHost();

  const inviteUrl = useMemo(() => {
    const baseUrl = getRuntimeOrigin();

    return `${baseUrl || ''}/?invite=${encodeURIComponent(inviterName)}`;
  }, [inviterName]);

  const inviteMessage = useMemo(() => {
    return `${inviterName} challenged you to beat a Snake run on ${hostLabel}. Pick a skin, jump into ${preferences.activeModeId}, and see if you can top ${topScore || 1}. ${inviteUrl}`;
  }, [hostLabel, inviterName, preferences.activeModeId, topScore, inviteUrl]);

  const copyInvite = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(inviteMessage);
      setFeedback('Invite message copied to clipboard.');
    } catch (error) {
      console.error(error);
      setFeedback('Copy failed. You can still copy the link manually.');
    }
  };

  const shareInvite = async (): Promise<void> => {
    if (!navigator.share) {
      setFeedback('Native sharing is not available here, so use the copy button instead.');
      return;
    }

    try {
      await navigator.share({
        title: 'Join my Snake challenge',
        text: inviteMessage,
        url: inviteUrl
      });
      setFeedback('Share sheet opened.');
    } catch (error) {
      console.error(error);
      setFeedback('Share was cancelled or unavailable.');
    }
  };

  return (
    <div className="page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Invite friends</p>
          <h1>Turn a score into a social challenge.</h1>
          <p>
            Generate a shareable landing URL plus a challenge message that works cleanly on both
            desktop and mobile.
          </p>
        </div>
      </section>

      <section className="invite-layout">
        <article className="invite-card">
          <span className="site-chip">Challenge link</span>
          <h2>{inviterName}&apos;s invite</h2>
          <p>{inviteMessage}</p>
          <div className="invite-card__url">{inviteUrl}</div>
          <div className="hero__actions">
            <button type="button" className="button button--primary" onClick={copyInvite}>
              Copy invite
            </button>
            <button type="button" className="button button--secondary" onClick={shareInvite}>
              Share from device
            </button>
          </div>
          {feedback ? <p className="inline-feedback">{feedback}</p> : null}
        </article>

        <article className="control-panel">
          <div className="section-heading section-heading--compact">
            <p className="eyebrow">Invite recipe</p>
            <h2>How this feels like a product feature</h2>
          </div>
          <div className="steps-grid">
            <article className="step-card">
              <span className="step-card__index">01</span>
              <h3>Use your profile</h3>
              <p>Your current nickname is embedded into the invite URL.</p>
            </article>
            <article className="step-card">
              <span className="step-card__index">02</span>
              <h3>Open on mobile</h3>
              <p>Friends can land on the home page from a phone and jump straight into play.</p>
            </article>
            <article className="step-card">
              <span className="step-card__index">03</span>
              <h3>Beat the score</h3>
              <p>The leaderboard records the resulting run and keeps the challenge moving.</p>
            </article>
          </div>
        </article>
      </section>
    </div>
  );
};

export default InvitePage;
